/**
 * Discount Code Form Custom Element
 * 
 * Handles AJAX submission of discount codes to Shopify cart
 * and emits events for other components to listen to.
 * 
 * Events:
 * - discount-code-form:applied - Emitted when a discount code is successfully applied
 * - discount-code-form:removed - Emitted when a discount code is successfully removed
 * - discount-code-form:error - Emitted when an error occurs
 */

if (!customElements.get('discount-code-form')) {
  class DiscountCodeForm extends HTMLElement {
    constructor() {
      super();
      this.form = null;
      this.input = null;
      this.messageContainer = null;
      this.messageElement = null;
      this.discountCodesList = null;
      this._isSubmitting = false;
    }

    connectedCallback() {
      this.initialize();
      this.setupEventListeners();
      
      // Listen for cart updates to refresh discount codes display
      if (window.Shopify && window.Shopify.cart) {
        this.cartUpdateHandler = this.handleCartUpdate.bind(this);
        window.addEventListener('cart:updated', this.cartUpdateHandler);
      }
      
      // Discount codes list is now handled by optimistic-cart.js
    }

    disconnectedCallback() {
      this.cleanup();
    }

    initialize() {
      // Find form
      this.form = this.querySelector('form');
      
      // Find input
      this.input = this.querySelector('input[name="discount"]');
      
      // Find message container
      this.messageContainer = this.querySelector('[data-message-container]');
      this.messageElement = this.querySelector('[data-message]');
      
      // Find discount codes list
      this.discountCodesList = this.querySelector('[data-discount-codes-list]');

      // Track section id for section rendering
      this.sectionId = this.dataset.sectionId || this.closest('[data-section-id]')?.dataset.sectionId || null;
    }

    setupEventListeners() {
      // Form submission
      if (this.form) {
        this.form.addEventListener('submit', this.handleFormSubmit.bind(this));
      }

      // Remove discount code buttons
      this.addEventListener('click', this.handleRemoveClick.bind(this));
    }

    handleFormSubmit(event) {
      event.preventDefault();
      
      if (this._isSubmitting) return;
      if (!this.input) return;

      const discountCode = this.input.value.trim();
      
      if (!discountCode) {
        this.showMessage('Please enter a discount code', 'error');
        return;
      }

      this.applyDiscountCode(discountCode);
    }

    async applyDiscountCode(code) {
      if (this._isSubmitting) return;
      
      this._isSubmitting = true;
      this.setLoadingState(true);
      this.#hideMessage();

      try {
        // Get existing discount codes from the DOM
        const existingDiscounts = this.#getExistingDiscounts();
        
        // Check if code already exists (case-insensitive)
        const codeExists = existingDiscounts.some(
          existingCode => existingCode.toLowerCase() === code.toLowerCase()
        );
        if (codeExists) {
          this.showMessage('Discount code already applied', 'error');
          this._isSubmitting = false;
          this.setLoadingState(false);
          return;
        }

        // Use original case for the code
        const requestedCodes = [...existingDiscounts, code];

        const updatedCart = await this.#updateCartDiscounts(requestedCodes);

        const status = this.#getDiscountStatus(updatedCart, code);

        if (status !== 'rejected') {
          this.#hideMessage();
          this.input.value = '';
          
          // Emit success event
          this.dispatchEvent(new CustomEvent('discount-code-form:applied', {
            detail: {
              code: code,
              cart: updatedCart,
              discount_applications: updatedCart?.discount_applications || []
            },
            bubbles: true
          }));
        } else if (status === 'rejected') {
          this.showMessage('Invalid discount code', 'error');
          
          // Emit error event
          this.dispatchEvent(new CustomEvent('discount-code-form:error', {
            detail: {
              code: code,
              message: 'Invalid discount code',
              cart: updatedCart
            },
            bubbles: true
          }));
        } else {
          this.#hideMessage();
          this.input.value = '';
          this.dispatchEvent(new CustomEvent('discount-code-form:applied', {
            detail: {
              code: code,
              cart: updatedCart,
              discount_applications: updatedCart?.discount_applications || []
            },
            bubbles: true
          }));
        }
      } catch (error) {
        console.error('DiscountCodeForm: Error applying discount code:', error);
        
        const errorMessage = error.message || error.description || 'Error applying discount code. Please try again.';
        this.showMessage(errorMessage, 'error');
        
        // Emit error event
        this.dispatchEvent(new CustomEvent('discount-code-form:error', {
          detail: {
            code: code,
            error: error,
            message: errorMessage
          },
          bubbles: true
        }));
      } finally {
        this._isSubmitting = false;
        this.setLoadingState(false);
      }
    }

    /**
     * Get existing discount codes from the DOM (maintains original case)
     * @private
     * @returns {string[]}
     */
    #getExistingDiscounts() {
      const discountCodes = [];
      const discountPills = this.querySelectorAll('[data-discount-code]');
      
      for (const pill of discountPills) {
        const code = pill.getAttribute('data-discount-code');
        if (code) {
          discountCodes.push(code); // Keep original case
        }
      }
      
      return discountCodes;
    }

    handleRemoveClick(event) {
      const removeButton = event.target.closest('[data-remove-discount]');
      if (!removeButton) return;

      event.preventDefault();
      const code = removeButton.getAttribute('data-remove-discount');
      
      if (code) {
        this.removeDiscountCode(code);
      }
    }

    async removeDiscountCode(code) {
      if (this._isSubmitting) return;
      
      this._isSubmitting = true;
      this.setLoadingState(true);

      try {
        // Get existing discount codes and remove the one being removed
        const existingDiscounts = this.#getExistingDiscounts();
        // Case-insensitive comparison
        const codeIndex = existingDiscounts.findIndex(
          existingCode => existingCode.toLowerCase() === code.toLowerCase()
        );
        
        if (codeIndex === -1) {
          // Code not found, nothing to remove
          this._isSubmitting = false;
          this.setLoadingState(false);
          return;
        }

        // Remove the code from the list
        existingDiscounts.splice(codeIndex, 1);

        // OPTIMISTIC UPDATE: Immediately remove from optimistic cart for instant UI feedback
        // This triggers a re-render which removes the pill before network request completes
        // The cartApi.update() handles both optimistic update AND network sync
        const updatedCart = await this.#updateCartDiscounts(existingDiscounts);
        
        // Emit removal event
        this.dispatchEvent(new CustomEvent('discount-code-form:removed', {
          detail: {
            code: code,
            cart: updatedCart
          },
          bubbles: true
        }));

      } catch (error) {
        console.error('DiscountCodeForm: Error removing discount code:', error);
        
        const errorMessage = error.message || error.description || 'Error removing discount code. Please try again.';
        this.showMessage(errorMessage, 'error');
        
        // Emit error event
        this.dispatchEvent(new CustomEvent('discount-code-form:error', {
          detail: {
            code: code,
            error: error,
            message: errorMessage
          },
          bubbles: true
        }));
      } finally {
        this._isSubmitting = false;
        this.setLoadingState(false);
      }
    }

    handleCartUpdate(event) {
      // Discount codes list is now handled by optimistic-cart.js
    }

    showMessage(message, type = 'info') {
      if (!this.messageContainer || !this.messageElement) return;

      this.messageElement.textContent = message;
      this.messageContainer.classList.remove('hidden');
      
      // Remove previous type classes
      this.messageElement.classList.remove('text-green-500', 'text-red-500', 'text-yellow-500');
      
      // Add appropriate type class
      if (type === 'success') {
        this.messageElement.classList.add('text-green-500');
      } else if (type === 'error') {
        this.messageElement.classList.add('text-red-500');
      } else {
        this.messageElement.classList.add('text-yellow-500');
      }

      // Auto-hide after 5 seconds
      setTimeout(() => {
        this.messageContainer.classList.add('hidden');
      }, 5000);
    }

    setLoadingState(loading) {
      if (this.input) {
        this.input.disabled = loading;
      }
      
      const button = this.form?.querySelector('button[type="submit"]');
      if (button) {
        button.disabled = loading;
        if (loading) {
          button.setAttribute('aria-busy', 'true');
        } else {
          button.removeAttribute('aria-busy');
        }
      }
    }

    cleanup() {
      // Remove event listeners if needed
      if (window.Shopify && window.Shopify.cart) {
        if (this.cartUpdateHandler) {
          window.removeEventListener('cart:updated', this.cartUpdateHandler);
          this.cartUpdateHandler = null;
        }
      }
    }

    #hideMessage() {
      if (this.messageContainer && this.messageElement) {
        this.messageElement.textContent = '';
        this.messageContainer.classList.add('hidden');
      }
    }

    #getDiscountStatus(cart, code) {
      if (!cart || !code) return 'pending';

      // First, check if the code is actually applied via discount applications
      const appliedCodes = this.#extractCartDiscountCodes(cart);
      // Case-insensitive comparison
      if (appliedCodes.some(appliedCode => appliedCode.toLowerCase() === code.toLowerCase())) {
        return 'applied';
      }

      // Then, check if Shopify explicitly rejected the code
      // cart.discount_codes contains codes with their applicable status
      const discountEntries = Array.isArray(cart.discount_codes)
        ? cart.discount_codes
        : [];
      const normalizedEntry = discountEntries.find((entry) => {
        const value = entry?.code || entry?.title;
        return value && value.toLowerCase() === code.toLowerCase();
      });

      // If code exists in discount_codes but applicable is false, it was rejected
      if (normalizedEntry?.applicable === false) {
        return 'rejected';
      }

      // If code exists in discount_codes with applicable=true but not in applications,
      // it might still be processing or there's an edge case
      if (normalizedEntry?.applicable === true) {
        // Double-check cart_level_discount_applications as source of truth
        if (Array.isArray(cart.cart_level_discount_applications)) {
          const hasApplication = cart.cart_level_discount_applications.some(
            app => app.type === 'discount_code' && 
                   (app.title?.toLowerCase() === code.toLowerCase() || app.code?.toLowerCase() === code.toLowerCase())
          );
          if (hasApplication) {
            return 'applied';
          }
        }
      }

      return 'pending';
    }

    #extractCartDiscountCodes(cart) {
      if (!cart) return [];

      const codes = new Set();

      const addCode = (value) => {
        if (!value) return;
        const normalized = value.toString().trim();
        if (normalized) {
          codes.add(normalized); // Keep original case
        }
      };

      // CRITICAL: Only extract codes from actual discount APPLICATIONS where they're applied
      // Do NOT use cart.discount_codes as it includes rejected codes with applicable:false
      // This matches the logic in discount-code-form.liquid (lines 24-38)

      // Check cart-level discount applications (primary source of truth)
      if (Array.isArray(cart.cart_level_discount_applications)) {
        cart.cart_level_discount_applications
          .filter((app) => app.type === 'discount_code')
          .forEach((app) => addCode(app.title || app.code));
      }

      // Check line-level discount allocations
      if (Array.isArray(cart.items)) {
        cart.items.forEach((item) => {
          if (Array.isArray(item.line_level_discount_allocations)) {
            item.line_level_discount_allocations
              .filter((allocation) => allocation.discount_application?.type === 'discount_code')
              .forEach((allocation) => addCode(allocation.discount_application?.title || allocation.discount_application?.code));
          }
        });
      }

      // Fallback: Check general discount_applications if cart_level not available
      if (codes.size === 0 && Array.isArray(cart.discount_applications)) {
        cart.discount_applications
          .filter((app) => app.type === 'discount_code')
          .forEach((app) => addCode(app.code || app.title));
      }

      return Array.from(codes);
    }


    async #updateCartDiscounts(discountCodes) {
      const discountParam = discountCodes.join(',');
      const cartApi = window.Shopify?.cart;
      const sectionsPayload = this.sectionId ? [this.sectionId] : undefined;

      if (cartApi && typeof cartApi.update === 'function') {
        try {
          const payload = { discount: discountParam };
          if (sectionsPayload) {
            payload.sections = sectionsPayload;
          }

          const response = await cartApi.update(payload);
          if (response) {
            return response;
          }

          if (typeof cartApi.get === 'function') {
            return await cartApi.get();
          }
        } catch (error) {
          // Fall back to direct fetch
        }
      }

      const requestBody = { discount: discountParam };

      if (this.sectionId) {
        requestBody.sections = sectionsPayload;
      }

      const fetchResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!fetchResponse.ok) {
        const error = await fetchResponse.json().catch(() => ({}));
        throw error;
      }

      const response = await fetchResponse.json();

      if (window.Shopify?.cart && typeof window.Shopify.cart._setCart === 'function') {
        try {
          window.Shopify.cart._setCart(response, true);
        } catch (error) {
          // Silently handle error
        }
      }

      // Synchronize optimistic cart if available
      if (window.Shopify?.cart && typeof window.Shopify.cart.forceSyncWithShopify === 'function') {
        try {
          await window.Shopify.cart.forceSyncWithShopify();
        } catch (error) {
          // Silently handle error
        }
      }

      return response;
    }
  }

  customElements.define('discount-code-form', DiscountCodeForm);
}

